import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GetSleepScreen } from '../../libs/GraphQL';
import SleepScreenVideoPreview from './SleepScreenVideoPreview';
import { formatDate } from './sleepScreenHelpers';
import styles from './SleepScreenDetail.module.css';

const SleepScreenDetail = () => {
  const { sleepScreenId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const [entry, setEntry] = useState(null);
  const [status, setStatus] = useState('loading');
  const [bannerInfo, setBannerInfo] = useState(() => {
    if (location.state && location.state.justUpdated) {
      return { fieldsChanged: location.state.fieldsChanged };
    }
    return null;
  });

  useEffect(() => {
    if (location.state && location.state.justUpdated) {
      history.replace({ pathname: location.pathname, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchOne = async () => {
      setStatus('loading');
      try {
        const result = await GetSleepScreen(sleepScreenId);
        if (cancelled) return;
        if (!result) {
          setEntry(null);
          setStatus('notFound');
        } else {
          setEntry(result);
          setStatus('loaded');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('getSleepScreen failed:', err);
        toast.error('Failed to load sleep screen.');
        setStatus('error');
      }
    };
    fetchOne();
    return () => {
      cancelled = true;
    };
  }, [sleepScreenId]);

  if (status === 'loading') {
    return (
      <main className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/sleep-screens">Library</Link>
        </div>
        <div className={styles.stateBlock}>Loading…</div>
      </main>
    );
  }

  if (status === 'notFound') {
    return (
      <main className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/sleep-screens">Library</Link> &gt; not found
        </div>
        <div className={styles.stateBlock}>
          No sleep screen with id <code>{sleepScreenId}</code> exists.
        </div>
      </main>
    );
  }

  if (status === 'error' || !entry) {
    return (
      <main className={styles.container}>
        <div className={styles.breadcrumb}>
          <Link to="/sleep-screens">Library</Link>
        </div>
        <div className={styles.stateBlock}>Couldn’t load this entry.</div>
      </main>
    );
  }

  return (
    <main className={styles.container}>
      <div className={styles.breadcrumb}>
        <Link to="/sleep-screens">Library</Link> &gt;{' '}
        {entry.displayName || entry.sleepScreenId}
      </div>

      <h1>Library Entry</h1>

      {bannerInfo && (
        <div className={styles.successBanner}>
          <span className={styles.successIcon}>✓</span>
          <div className={styles.successContent}>
            <strong>Changes saved</strong>
            <span>
              Updated {bannerInfo.fieldsChanged}{' '}
              {bannerInfo.fieldsChanged === 1 ? 'field' : 'fields'} on{' '}
              {entry.displayName || entry.sleepScreenId}
            </span>
          </div>
          <button
            type="button"
            className={styles.bannerDismiss}
            onClick={() => setBannerInfo(null)}
            aria-label="dismiss"
          >
            ×
          </button>
        </div>
      )}

      <section className={styles.topSection}>
        <SleepScreenVideoPreview url={entry.videoUrl} />
        <div className={styles.topMeta}>
          <div className={styles.displayName}>{entry.displayName || '—'}</div>
          <div className={styles.id}>{entry.sleepScreenId}</div>
        </div>
        <Link
          to={`/sleep-screens/${encodeURIComponent(entry.sleepScreenId)}/edit`}
          className={styles.editButton}
        >
          edit entry
        </Link>
      </section>

      <section className={styles.detailsCard}>
        <h2>Details</h2>
        <dl className={styles.detailsGrid}>
          <dt>Name</dt>
          <dd>{entry.displayName || '—'}</dd>
          <dt>ID</dt>
          <dd className={styles.mono}>{entry.sleepScreenId}</dd>
          <dt>Description</dt>
          <dd>{entry.description || '—'}</dd>
          <dt>Video URL</dt>
          <dd className={styles.mono}>
            {entry.videoUrl ? (
              <a
                href={entry.videoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {entry.videoUrl}
              </a>
            ) : (
              '—'
            )}
          </dd>
          <dt>Created on</dt>
          <dd>{formatDate(entry.createdAt, entry.createdBy)}</dd>
          <dt>Last edited on</dt>
          <dd>{formatDate(entry.updatedAt, entry.updatedBy)}</dd>
        </dl>
      </section>
    </main>
  );
};

export default SleepScreenDetail;
